/* eslint-disable @typescript-eslint/no-explicit-any */
import { Checkbox, FormError, Label } from '@kcftech/design-system';
import { GroupDropdown } from 'components/dropdown/GroupDropdown';
import { DurationInput } from 'components/forms/DurationInput';
import { CreateNotificationSubscriptionFormInputProps } from 'pages/Settings/NotificationSubscriptions/form/types';
import { UnacknowlededPeriodInfoPopup } from 'pages/Settings/NotificationSubscriptions/IndicatorAlerts/shared';
import {
    NotificationTriggerTable,
    NotificationTriggerTableWrapper,
} from 'pages/Settings/NotificationSubscriptions/NotificationTriggerTable';
import {
    NotificationEventCategory,
    notificationEventCategoryName,
} from 'pages/Settings/NotificationSubscriptions/shared';
import { useState } from 'react';
import { Controller, FieldError } from 'react-hook-form';
import { TimeSpan } from 'utils/TimeSpan';

export function IndicatorAlertsNotificationSubscriptionFormContent({
    form,
    location,
}: CreateNotificationSubscriptionFormInputProps) {
    const {
        control,
        formState: { errors, isSubmitted },
        watch,
        trigger,
    } = form;

    const watchedValues = watch();

    const [group, setGroup] = useState<{ Id: string; Name: string } | undefined>();

    const withFormValidate = (onChange: (e: unknown) => void) => (e: unknown) => {
        onChange(e);
        if (isSubmitted) {
            trigger();
        }
    };

    return (
        <>
            <Label label="Group" htmlFor="groupId" />
            <Controller
                control={control}
                name="indicatorAlerts.groupId"
                render={({ field }) => (
                    <GroupDropdown
                        id="groupId"
                        value={group}
                        onChange={(newGroup) => {
                            field.onChange(newGroup.Id);
                            setGroup(newGroup);
                        }}
                        className="full-width mb-15"
                        disabled={!location}
                        title={!location ? 'Please select a location first' : undefined}
                        locationId={watchedValues.location?.Id ?? ''}
                        defaultValueText="All Systems"
                        allowSelectNone
                    />
                )}
            />
            <FormError message={errors.indicatorAlerts?.groupId?.message} />

            <NotificationTriggerTableWrapper className="mb-15">
                <NotificationTriggerTable
                    rows={Object.values(NotificationEventCategory).map((category) => (
                        <tr key={category}>
                            <td>{notificationEventCategoryName[category]}</td>
                            <td className="text-right">
                                <Controller
                                    control={control}
                                    name={
                                        `indicatorAlerts.notificationTriggers.${category}.IsEmailEnabled` as const
                                    }
                                    render={({ field: { value, onChange } }) => (
                                        <Checkbox
                                            label=""
                                            onChange={withFormValidate(onChange)}
                                            checked={value}
                                            title={`Toggle emails for ${notificationEventCategoryName[category]}`}
                                        />
                                    )}
                                />
                            </td>
                            <td className="text-right">
                                <Controller
                                    control={control}
                                    name={
                                        `indicatorAlerts.notificationTriggers.${category}.IsSmsEnabled` as const
                                    }
                                    render={({ field: { value, onChange } }) => (
                                        <Checkbox
                                            label=""
                                            onChange={withFormValidate(onChange)}
                                            checked={value}
                                            title={`Toggle text messages for ${notificationEventCategoryName[category]}`}
                                        />
                                    )}
                                />
                            </td>
                        </tr>
                    ))}
                />

                {/* Need to cast to unknown and then any because react hook form doesn't contain the correct type for errors */}
                {((errors as unknown) as any)?.notificationTriggers?.map(
                    (errText: FieldError) => (
                        <FormError message={errText.message} key={errText.message} />
                    )
                )}
                <FormError
                    message={
                        ((errors as unknown) as any)?.notificationTriggerPhoneNumber
                            ?.message
                    }
                />
            </NotificationTriggerTableWrapper>

            <Controller
                control={control}
                name="indicatorAlerts.unacknowledgedPeriodMs"
                // At present, we cannot apply validation rules here, as the
                // validator gets clobbered by the Resolver used on this form.
                // For now, validation of this prop should happen in the form's
                // resolver func
                render={({ field: { value, onChange } }) => (
                    <UnacknowledgedPeriodFormInput valueMs={value} onChange={onChange} />
                )}
            />
            <FormError
                message={
                    ((errors.indicatorAlerts as any)?.unacknowledgedPeriodMs as
                        | FieldError
                        | undefined)?.message
                }
            />
        </>
    );
}

export function UnacknowledgedPeriodFormInput({
    valueMs,
    onChange,
}: Required<{ valueMs: number; onChange: (newValueMs: number) => void }>) {
    return (
        <div>
            <Label
                label={
                    <>
                        Unacknowledged Period (days hh:mm){' '}
                        <UnacknowlededPeriodInfoPopup />
                    </>
                }
            >
                <DurationInput
                    value={TimeSpan.fromMillis(valueMs)}
                    onChange={(valTimeSpan) => onChange(valTimeSpan.asMilliseconds())}
                    showDays
                />
            </Label>
        </div>
    );
}

const MaxUnacknowledgedPeriodDays = 30;
export const unacknowledgedPeriodRules = {
    validate: (valueMs: number) => {
        const valAsDays = TimeSpan.fromMillis(valueMs).asDays();
        if (valAsDays < 0) {
            return 'Unacknowledged Period must be greater than or equal to 0 days.';
        }
        if (valAsDays > MaxUnacknowledgedPeriodDays) {
            return `Unacknowledged Period must be less than or equal to ${MaxUnacknowledgedPeriodDays} days.`;
        }
        return undefined;
    },
};
